import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Grid,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  MenuItem,
  Switch,
  Slider,
  Snackbar,
  Alert,
  Chip,
  Paper,
} from "@mui/material";

import { config } from "@config";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { getPersonas, Persona } from "../../lib/api/Persona";
import { getPersonaDisplayName } from "../../lib/helpers/personaDisplay";
import { useCompanion } from '../../lib/hooks/UseCompanion';
import {
  DEFAULT_COMPANION_SETTINGS,
  mapAiPreset,
  type AiPreset,
  type GuidanceLens,
  type AnswerLength,
  type GoalPursuitPreference,
  type GoalUserOverride,
  type PersonalizationLevel,
} from "@common/domain/types";
import { API_URL } from "../../lib/constants";

const PRESETS = {
  everyday_coach: {
    label: "Everyday Coach (Default)",
    description: "Balanced · Practical lens · LOTE-only",
  },
  deep_dive: {
    label: "Deep Dive (Study)",
    description: "Precise · Scripture-forward · Citations required · LOTE-only",
  },
};

const GUIDANCE_LENSES = [
  { value: "practical", label: "Practical / Everyday" },
  { value: "integrated", label: "Integrated (practical + spiritual)" },
  { value: "scripture_forward", label: "Scripture-forward / Discipleship" },
];

interface AvailableGoal {
  id: string;
  name: string;
  alignmentStatement: string;
  scope: string;
  active: boolean;
  userVisibility: string;
}

const ANSWER_LENGTH_OPTIONS: Array<{
  value: AnswerLength;
  label: string;
  description: string;
}> = [
  {
    value: "short",
    label: "Short",
    description: "Brief, direct answers.",
  },
  {
    value: "medium",
    label: "Medium",
    description: "Balanced answers with a little context.",
  },
  {
    value: "long",
    label: "Long",
    description: "More detail and explanation.",
  },
  {
    value: "comprehensive",
    label: "Complete",
    description: "Thorough answers with deeper context.",
  },
];

const CITATIONS_STRICTNESS_OPTIONS = [
  {
    value: "Low",
    label: "Low",
    description: "Citations are looser and used only when helpful.",
  },
  {
    value: "Balanced",
    label: "Balanced",
    description: "Citations are preferred for grounded claims.",
  },
  {
    value: "Strict",
    label: "Strict",
    description: "Citations are expected for spiritual and practical claims.",
  },
];

const PERSONALIZATION_OPTIONS: Array<{
  value: PersonalizationLevel;
  label: string;
  description: string;
}> = [
  {
    value: "loose",
    label: "Loose",
    description: "Use personal claims only when they clearly help.",
  },
  {
    value: "balanced",
    label: "Balanced",
    description: "Prefer relevant claims when they fit naturally.",
  },
  {
    value: "required",
    label: "Required",
    description: "Use at least one available claim when possible.",
  },
];

const PERSONALIZATION_MARKS = PERSONALIZATION_OPTIONS.map((option, index) => ({
  value: index,
  label: option.label,
}));

const ANSWER_LENGTH_MARKS = ANSWER_LENGTH_OPTIONS.map((option, index) => ({
  value: index,
  label: option.label,
}));

const CITATIONS_STRICTNESS_MARKS = CITATIONS_STRICTNESS_OPTIONS.map((option, index) => ({
  value: index,
  label: option.label,
}));

const DEFAULT_PERSONALIZATION_INDEX = 1;
const DEFAULT_ANSWER_LENGTH_INDEX = 1;
const DEFAULT_CITATIONS_STRICTNESS_INDEX = 1;

const SETTINGS_SLIDER_SX = {
  "& .MuiSlider-rail": {
    background: "linear-gradient(90deg, #8fb7d3 0%, #b89b5e 50%, #b65f4c 100%)",
    opacity: 0.35,
  },
  "& .MuiSlider-track": {
    background: "linear-gradient(90deg, #8fb7d3 0%, #b89b5e 50%, #b65f4c 100%)",
    border: "none",
  },
  "& .MuiSlider-thumb": {
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.25)",
  },
  "& .MuiSlider-markLabel": {
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  },
};

const getAnswerLengthIndex = (level: AnswerLength) => {
  const index = ANSWER_LENGTH_OPTIONS.findIndex((option) => option.value === level);
  return index >= 0 ? index : DEFAULT_ANSWER_LENGTH_INDEX;
};

const getAnswerLengthOption = (level: AnswerLength) =>
  ANSWER_LENGTH_OPTIONS[getAnswerLengthIndex(level)] ?? ANSWER_LENGTH_OPTIONS[DEFAULT_ANSWER_LENGTH_INDEX];

const getCitationsStrictnessIndex = (level: string) => {
  const index = CITATIONS_STRICTNESS_OPTIONS.findIndex((option) => option.value === level);
  return index >= 0 ? index : DEFAULT_CITATIONS_STRICTNESS_INDEX;
};

const getCitationsStrictnessOption = (level: string) =>
  CITATIONS_STRICTNESS_OPTIONS[getCitationsStrictnessIndex(level)] ?? CITATIONS_STRICTNESS_OPTIONS[DEFAULT_CITATIONS_STRICTNESS_INDEX];

const getPersonalizationIndex = (level: PersonalizationLevel) => {
  const index = PERSONALIZATION_OPTIONS.findIndex((option) => option.value === level);
  return index >= 0 ? index : DEFAULT_PERSONALIZATION_INDEX;
};

const getPersonalizationOption = (level: PersonalizationLevel) =>
  PERSONALIZATION_OPTIONS[getPersonalizationIndex(level)] ?? PERSONALIZATION_OPTIONS[DEFAULT_PERSONALIZATION_INDEX];

interface ResponseLengthControlProps {
  answerLength: AnswerLength;
  setAnswerLength: (value: AnswerLength) => void;
}

const ResponseLengthControl: React.FC<ResponseLengthControlProps> = ({
  answerLength,
  setAnswerLength,
}) => {
  const answerLengthIndex = getAnswerLengthIndex(answerLength);
  const answerLengthDescription = getAnswerLengthOption(answerLength).description;

  return (
    <Grid item xs={12}>
      <Typography
        variant="body2"
        sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500, textAlign: 'left' }}
      >
        Response Length
      </Typography>
      <Box sx={{ px: 1.5, pt: 0.5 }}>
        <Slider
          min={0}
          max={3}
          step={1}
          marks={ANSWER_LENGTH_MARKS}
          value={answerLengthIndex}
          onChange={(_event, value) => {
            const nextIndex = Array.isArray(value) ? value[0] : value;
            const nextOption = ANSWER_LENGTH_OPTIONS[nextIndex] ?? ANSWER_LENGTH_OPTIONS[DEFAULT_ANSWER_LENGTH_INDEX];
            setAnswerLength(nextOption.value);
          }}
          aria-label="Response length"
          sx={SETTINGS_SLIDER_SX}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'left' }}>
        {answerLengthDescription}
      </Typography>
    </Grid>
  );
};

const CompanionSettings: React.FC = () => {
  const { getCompanionSettings, updateCompanionSettings } = useCompanion();
  const { userInfo } = useUserInfoContext();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string>("");

  const [aiPreset, setAiPreset] = useState<AiPreset>(DEFAULT_COMPANION_SETTINGS.aiPreset);
  const [guidanceLens, setGuidanceLens] = useState("integrated");
  const [answerLength, setAnswerLength] = useState<AnswerLength>("medium");
  const [citationsStrictness, setCitationsStrictness] = useState("Balanced");
  const [webSearchOnUnaligned, setWebSearchOnUnaligned] = useState(
    DEFAULT_COMPANION_SETTINGS.webSearchOnUnaligned,
  );
  const [personalizationLevel, setPersonalizationLevel] = useState<PersonalizationLevel>(
    DEFAULT_COMPANION_SETTINGS.personalizationLevel,
  );
  const [savedOpen, setSavedOpen] = useState(false);

  // Goal state — single model replacing goalPursuit + userOverride
  const [goalMode, setGoalMode] = useState<'persona_goals' | 'custom_goal' | 'off'>('persona_goals');
  const [customGoalId, setCustomGoalId] = useState<string>('');
  const [availableGoals, setAvailableGoals] = useState<AvailableGoal[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);

  useEffect(() => {
    if (userInfo) {
      const fetchPersonas = async () => {
        try {
          const response = await getPersonas(userInfo);
          setPersonas(response);
          if (response.length > 0) {
            const learningPersonaId = config.learningPersonaId;
            const defaultPersona = (learningPersonaId && response.find(p => p.id === learningPersonaId)) || response.find(p => p.id === 'p072690a2e4b') || response[0];
            setSelectedPersona(defaultPersona?.id ?? "");
          }
        } catch (error) {
          console.error("Error fetching personas:", error);
        }
      };
      fetchPersonas();
    }
  }, [userInfo]);

  const loadAvailableGoals = useCallback(async () => {
    if (config.showResponseLengthOnly) return;
    if (!userInfo?.accessToken || !selectedPersona) return;
    setGoalsLoading(true);
    try {
      const res = await fetch(`${API_URL}/avatars/${selectedPersona}/available-goals`, {
        headers: { Authorization: `Bearer ${userInfo.accessToken}` },
      });
      const data = await res.json();
      setAvailableGoals(data?.goals ?? []);

      const pursuit = data?.goalPursuit ?? 'default';
      const override = data?.userOverride ?? 'default_org_goal';
      const customId = data?.customUserGoalId ?? '';

      if (pursuit === 'companion_only' || override === 'companion_only') {
        setGoalMode('off');
      } else if (override === 'custom_goal' && customId) {
        setGoalMode('custom_goal');
        setCustomGoalId(customId);
      } else {
        setGoalMode('persona_goals');
      }
    } catch (e) {
      console.error('Failed to load available goals', e);
    } finally {
      setGoalsLoading(false);
    }
  }, [userInfo?.accessToken, selectedPersona]);

  useEffect(() => {
    if (selectedPersona) {
      const fetchCompanionSettings = async () => {
        try {
          const companionSettings = await getCompanionSettings(selectedPersona);
          if (companionSettings) {
            setAiPreset(mapAiPreset(companionSettings.aiPreset));
            setGuidanceLens(companionSettings.guidanceLens);
            setAnswerLength(companionSettings.answerLength as AnswerLength);
            setCitationsStrictness(companionSettings.citationsStrictness);
            setWebSearchOnUnaligned(
              companionSettings.webSearchOnUnaligned ?? DEFAULT_COMPANION_SETTINGS.webSearchOnUnaligned,
            );
            setPersonalizationLevel(
              companionSettings.personalizationLevel ?? DEFAULT_COMPANION_SETTINGS.personalizationLevel,
            );
          }
        } catch (error) {
          console.error("Failed to fetch companion settings:", error);
        }
      };
      fetchCompanionSettings();
      loadAvailableGoals();
    }
  }, [selectedPersona, getCompanionSettings, loadAvailableGoals]);

  const handleSave = () => {
    if (config.showResponseLengthOnly) {
      updateCompanionSettings(selectedPersona, { answerLength: answerLength as AnswerLength });
      setSavedOpen(true);
      return;
    }

    let goalPursuit: GoalPursuitPreference = 'default';
    let userOverride: GoalUserOverride = 'default_org_goal';
    let customUserGoalId: string | undefined;

    if (goalMode === 'off') {
      goalPursuit = 'companion_only';
      userOverride = 'companion_only';
    } else if (goalMode === 'custom_goal' && customGoalId) {
      goalPursuit = 'default';
      userOverride = 'custom_goal';
      customUserGoalId = customGoalId;
    } else {
      goalPursuit = 'default';
      userOverride = 'default_org_goal';
    }

    const updates = {
      aiPreset: aiPreset as AiPreset,
      guidanceLens: guidanceLens as GuidanceLens,
      answerLength: answerLength as AnswerLength,
      citationsStrictness: citationsStrictness as string,
      webSearchOnUnaligned,
      goalPursuit,
      userOverride,
      customUserGoalId,
      personalizationLevel,
    };
    updateCompanionSettings(selectedPersona, updates);
    setSavedOpen(true);
  };

  const orgGoals = availableGoals.filter((g) => g.scope === 'org');
  const userGoals = availableGoals.filter((g) => g.scope === 'user');
  const personalizationIndex = getPersonalizationIndex(personalizationLevel);
  const personalizationDescription = getPersonalizationOption(personalizationLevel).description;
  const answerLengthIndex = getAnswerLengthIndex(answerLength);
  const answerLengthDescription = getAnswerLengthOption(answerLength).description;
  const citationsStrictnessIndex = getCitationsStrictnessIndex(citationsStrictness);
  const citationsStrictnessDescription = getCitationsStrictnessOption(citationsStrictness).description;

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Grid container spacing={2}>
        {(personas.length > 1 || !config.showResponseLengthOnly) && (
          <Grid item xs={12}>
            <Typography variant="body2" align="left" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
              Persona
            </Typography>
            <TextField
              select
              fullWidth
              value={selectedPersona}
              onChange={(e) => setSelectedPersona(e.target.value)}
            >
              {[...personas].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((persona) => (
                <MenuItem key={persona.id} value={persona.id}>
                  {getPersonaDisplayName(persona)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {config.showResponseLengthOnly ? (
          <ResponseLengthControl answerLength={answerLength} setAnswerLength={setAnswerLength} />
        ) : (
          <>
            {/* Goals section — replaces goalPursuit, userOverride, and ActiveGoalsModal */}
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
            Goals
          </Typography>
          <TextField
            select
            fullWidth
            value={goalMode}
            onChange={(e) => {
              const mode = e.target.value as typeof goalMode;
              setGoalMode(mode);
              if (mode !== 'custom_goal') setCustomGoalId('');
            }}
            sx={{ mb: 1.5 }}
          >
            <MenuItem value="persona_goals">Use persona / org goals</MenuItem>
            <MenuItem value="custom_goal">Use a specific goal</MenuItem>
            <MenuItem value="off">No goals (companion only)</MenuItem>
          </TextField>

          {goalMode === 'custom_goal' && (
            <TextField
              select
              fullWidth
              value={customGoalId}
              onChange={(e) => setCustomGoalId(e.target.value)}
              label="Select your goal"
              sx={{ mb: 1.5 }}
            >
              <MenuItem value="">
                <em>Choose a goal...</em>
              </MenuItem>
              {availableGoals.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {g.name}
                    <Chip size="small" label={g.scope} variant="outlined" />
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}

          {goalsLoading ? (
            <Typography variant="body2" color="text.secondary">Loading goals...</Typography>
          ) : availableGoals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No goals are configured for this persona.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {goalMode === 'off'
                  ? 'Goal pursuit is turned off. These goals are available but will not be active.'
                  : goalMode === 'custom_goal'
                    ? 'Only the selected goal above will be active.'
                    : 'These goals are active for your conversations.'}
              </Typography>
              {orgGoals.length > 0 && (
                <>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Organization goals
                  </Typography>
                  {orgGoals.map((g) => {
                    const isActive = goalMode === 'persona_goals'
                      ? g.active
                      : goalMode === 'custom_goal'
                        ? g.id === customGoalId
                        : false;
                    return (
                      <Box key={g.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, pl: 0.5 }}>
                        <Chip
                          size="small"
                          label={isActive ? 'active' : 'inactive'}
                          color={isActive ? 'success' : 'default'}
                          sx={{ mt: 0.3 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500}>{g.name}</Typography>
                          {g.alignmentStatement && (
                            <Typography variant="caption" color="text.secondary">{g.alignmentStatement}</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </>
              )}
              {userGoals.length > 0 && (
                <>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                    Your personal goals
                  </Typography>
                  {userGoals.map((g) => {
                    const isActive = goalMode === 'persona_goals'
                      ? g.active
                      : goalMode === 'custom_goal'
                        ? g.id === customGoalId
                        : false;
                    return (
                      <Box key={g.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, pl: 0.5 }}>
                        <Chip
                          size="small"
                          label={isActive ? 'active' : 'inactive'}
                          color={isActive ? 'success' : 'default'}
                          sx={{ mt: 0.3 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500}>{g.name}</Typography>
                          {g.alignmentStatement && (
                            <Typography variant="caption" color="text.secondary">{g.alignmentStatement}</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </>
              )}
            </Paper>
          )}
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" align="left" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                    AI Preset
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={aiPreset}
                    onChange={(e) => setAiPreset(e.target.value as AiPreset)}
                  >
                    {Object.entries(PRESETS).map(([key, { label }]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <ResponseLengthControl answerLength={answerLength} setAnswerLength={setAnswerLength} />
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500, textAlign: 'left' }}
                  >
                    Web Search (experimental)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={webSearchOnUnaligned}
                        onChange={(e) => setWebSearchOnUnaligned(e.target.checked)}
                      />
                    }
                    label='Allow web search for out-of-scope prompts'
                    sx={{ m: 0, justifyContent: 'flex-start', width: '100%' }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" align="left" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                    Guidance Lens
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={guidanceLens}
                    onChange={(e) => setGuidanceLens(e.target.value)}
                  >
                    {GUIDANCE_LENSES.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500, textAlign: 'left' }}
                  >
                    Citations Strictness
                  </Typography>
                  <Box sx={{ px: 1.5, pt: 0.5 }}>
                    <Slider
                      min={0}
                      max={2}
                      step={1}
                      marks={CITATIONS_STRICTNESS_MARKS}
                      value={citationsStrictnessIndex}
                      onChange={(_event, value) => {
                        const nextIndex = Array.isArray(value) ? value[0] : value;
                        const nextOption = CITATIONS_STRICTNESS_OPTIONS[nextIndex] ?? CITATIONS_STRICTNESS_OPTIONS[DEFAULT_CITATIONS_STRICTNESS_INDEX];
                        setCitationsStrictness(nextOption.value);
                      }}
                      aria-label="Citations strictness"
                      sx={SETTINGS_SLIDER_SX}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'left' }}>
                    {citationsStrictnessDescription}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500, textAlign: 'left' }}
                  >
                    Personalization (experimental)
                  </Typography>
                  <Box sx={{ px: 1.5, pt: 0.5 }}>
                    <Slider
                      min={0}
                      max={2}
                      step={1}
                      marks={PERSONALIZATION_MARKS}
                      value={personalizationIndex}
                      onChange={(_event, value) => {
                        const nextIndex = Array.isArray(value) ? value[0] : value;
                        const nextOption = PERSONALIZATION_OPTIONS[nextIndex] ?? PERSONALIZATION_OPTIONS[DEFAULT_PERSONALIZATION_INDEX];
                        setPersonalizationLevel(nextOption.value);
                      }}
                      aria-label="Personalization level"
                      sx={SETTINGS_SLIDER_SX}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'left' }}>
                    {personalizationDescription}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!selectedPersona}
          >
            Save Settings
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        open={savedOpen}
        autoHideDuration={3000}
        onClose={() => setSavedOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSavedOpen(false)} sx={{ width: '100%' }}>
          {config.showResponseLengthOnly ? "Response length setting saved" : "Companion settings saved"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanionSettings;
